import type { Node as PmNode } from '@tiptap/pm/model';
import type { Finding } from '../cms/shared';

/**
 * Finding a copy-edit quote in what's being edited (backlog f2261d58). A quote
 * is verbatim from the text the check read, which collapses whitespace and
 * doesn't know where bold or a link starts, so it's matched on the words.
 */

export interface TextRange {
  from: number;
  to: number;
}

/** A line the check marks up: "# ", "- ", "> ". The prompt forbids quoting them; models still do. */
export const LINE_MARKER = /^(?:#{1,6}\s|-\s|>\s)/;
const SPACE = /[\s ]/;

/** The first match of `quote` in `text`, at or after `start`, ignoring runs of whitespace. */
export function locateQuote(text: string, quote: string, start = 0): TextRange | null {
  const want = quote.trim().replace(/[\s ]+/g, ' ');
  if (!want) return null;
  // Word for word first, so a quote's own edge spaces go with it: "very " → "".
  const exact = text.indexOf(quote, start);
  if (exact >= 0) return { from: exact, to: exact + quote.length };
  let flat = '';
  const at: number[] = [];
  for (let i = start; i < text.length; i += 1) {
    if (SPACE.test(text[i])) {
      if (flat.endsWith(' ')) continue;
      flat += ' ';
    } else {
      flat += text[i];
    }
    at.push(i);
  }
  const hit = flat.indexOf(want);
  if (hit >= 0) return { from: at[hit], to: at[hit + want.length - 1] + 1 };
  return LINE_MARKER.test(want) ? locateQuote(text, want.replace(LINE_MARKER, ''), start) : null;
}

const flatWords = (s: string) => s.replace(/[\s\u00a0]+/g, ' ').trim();
const wordSet = (s: string) => new Set(s.toLowerCase().match(/[\p{L}\p{N}]+/gu) || []);

/**
 * Of the places a quote was found, the one most like the line the check read.
 * Not an exact match: another fix may have changed that line since.
 */
export function closestToLine<T>(items: T[], textOf: (item: T) => string, line?: string | null) {
  if (!line || items.length < 2) return items[0] ?? null;
  const want = wordSet(line);
  const score = (item: T) => {
    const have = wordSet(textOf(item));
    let shared = 0;
    want.forEach((w) => {
      if (have.has(w)) shared += 1;
    });
    return shared - Math.abs(have.size - want.size) * 0.001;
  };
  let best = items[0];
  let bestScore = score(best);
  items.slice(1).forEach((item) => {
    const s = score(item);
    if (s > bestScore) {
      best = item;
      bestScore = s;
    }
  });
  return best;
}

/** Each paragraph, heading or list item once per version of the document. */
interface TextBlock {
  text: string;
  flat: string;
  places: number[];
}
const blocksByDoc = new WeakMap<object, TextBlock[]>();
function textBlocks(doc: PmNode): TextBlock[] {
  const known = blocksByDoc.get(doc);
  if (known) return known;
  const blocks: TextBlock[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    // Each character's place in the document; anything that isn't text is a gap.
    let text = '';
    const places: number[] = [];
    node.forEach((child, offset) => {
      const inner = pos + 1 + offset;
      if (child.isText) {
        for (let i = 0; i < (child.text || '').length; i += 1) places.push(inner + i);
        text += child.text;
      } else {
        places.push(inner);
        text += ' ';
      }
    });
    blocks.push({ text, flat: flatWords(text), places });
    return false;
  });
  blocksByDoc.set(doc, blocks);
  return blocks;
}

/** Punctuation alone: with no line to place it, the first one on the page is a guess. */
const isBareMark = (quote: string) => !/[\p{L}\p{N}]/u.test(quote);

/** Where a quote is in the document: in the block most like its line, when it's known. */
export function locateInDoc(doc: PmNode, quote: string, line?: string | null): TextRange | null {
  if (isBareMark(quote) && !line) return null;
  const want = flatWords(quote).replace(LINE_MARKER, '');
  const hits = textBlocks(doc).filter((b) => b.flat.includes(want));
  const block = closestToLine(hits, (b) => b.flat, line);
  const hit = block && locateQuote(block.text, quote);
  return hit ? { from: block.places[hit.from], to: block.places[hit.to - 1] + 1 } : null;
}

/**
 * What Apply would put in the text. The check sometimes writes an instruction
 * instead of words ("[remove line]"), which must never be typed into the article:
 * null means there's nothing to apply, '' means take the words out.
 */
export function suggestionText(f: Finding): string | null {
  if (typeof f.suggestion !== 'string') return null;
  const s = f.suggestion.trim();
  if (/^\[(remove|delete|omit|cut)\b[^\]]*\]$/i.test(s)) return '';
  return /^\[[^\]]*\]$/.test(s) ? null : f.suggestion;
}

/**
 * The check reads an empty bullet as a line holding only "-". That's no hyphen,
 * so it isn't looked for as text: it's the list items with nothing in them.
 */
export function isEmptyBullets(f: Finding) {
  const quote = (f.quote || '').trim();
  if (quote !== '-') return false;
  if (typeof f.line === 'string' && f.line.trim() !== quote) return false;
  return !suggestionText(f);
}

const isEmptyItem = (node: PmNode) =>
  node.type?.name === 'listItem' && node.childCount === 1 && node.firstChild?.content.size === 0;

/** The empty list items in the document, in order. */
export function emptyBullets(doc: PmNode): TextRange[] {
  const out: TextRange[] = [];
  doc.descendants((node, pos) => {
    if (isEmptyItem(node)) out.push({ from: pos, to: pos + node.nodeSize });
    return true;
  });
  return out;
}

/** What to delete so they're gone: a list with nothing else in it goes whole. */
export function emptyBulletCuts(doc: PmNode): TextRange[] {
  const out: TextRange[] = [];
  doc.descendants((node, pos) => {
    if (/List$/.test(node.type?.name || '')) {
      let all = node.childCount > 0;
      node.forEach((item) => {
        if (!isEmptyItem(item)) all = false;
      });
      if (all) {
        out.push({ from: pos, to: pos + node.nodeSize });
        return false;
      }
    }
    if (isEmptyItem(node)) out.push({ from: pos, to: pos + node.nodeSize });
    return true;
  });
  return out;
}

/** The text fields a finding's `path` can name, beside the body. */
export type CopyEditField = 'title' | 'summary' | 'seo.metaTitle' | 'seo.metaDescription';

export type DraftTexts = Record<CopyEditField, string>;

/** Is this finding about text that's still there? An applied one no longer is. */
export function stillInDraft(f: Finding, doc: PmNode | null, texts: DraftTexts) {
  if (isEmptyBullets(f)) return doc ? emptyBullets(doc).length > 0 : true;
  if (f.source !== 'copy_edit' || !f.quote) return true;
  if (f.path && f.path in texts)
    return Boolean(locateQuote(texts[f.path as CopyEditField], f.quote));
  return doc ? Boolean(locateInDoc(doc, f.quote, f.line)) : true;
}
